import React, { useRef, useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import useSpecialSyncImport from '../../hooks/useSpecialSyncImport';
import { useAuth } from '../../context/authContext';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Stack,
} from '@mui/material';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const Section = ({ title, rows, renderRow, onSelectAll, allSelected, countLabel }) => {
  return (
    <Box sx={{ mb: 3, ...viewRootTextPrimarySx }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='h6'>{title}</Typography>
        {rows && rows.length > 0 && (
          <FormControlLabel
            control={<Checkbox checked={allSelected} onChange={onSelectAll} />}
            label='Alle auswählen'
          />
        )}
      </Stack>
      <Typography variant='caption' sx={{ opacity: 0.8 }} gutterBottom>
        {countLabel}
      </Typography>
      <Paper variant='outlined'>
        <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Aktion</TableCell>
                {renderRow('header')}
              </TableRow>
            </TableHead>
            <TableBody>{rows && rows.map(renderRow)}</TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
};

const SpecialSyncImport = () => {
  const fileRef = useRef();
  const { analyze, execute } = useSpecialSyncImport();
  const auth = useAuth();

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  // selections
  const [selectedChangeKeys, setSelectedChangeKeys] = useState(() => new Set());
  const [selectedPlanned, setSelectedPlanned] = useState(() => new Set());
  const [selectedDeactivateNow, setSelectedDeactivateNow] = useState(() => new Set());
  const [selectedCreate, setSelectedCreate] = useState(() => new Set());
  const [selectedNewEmployees, setSelectedNewEmployees] = useState(() => new Set());
  const [selectedIgnoreChangeKeys, setSelectedIgnoreChangeKeys] = useState(() => new Set());

  const readFileAsText = useCallback(file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file, 'utf-8');
    });
  }, []);

  const onUpload = useCallback(async () => {
    if (!fileRef.current || !fileRef.current.files || fileRef.current.files.length === 0) {
      toast.info('Bitte CSV-Datei auswählen');
      return;
    }
    const file = fileRef.current.files[0];
    try {
      setLoading(true);
      const content = await readFileAsText(file);
      const a = await analyze(content);
      setAnalysis(a);
      setSelectedChangeKeys(new Set());
      setSelectedPlanned(new Set());
      setSelectedDeactivateNow(new Set());
      setSelectedCreate(new Set());
      if (a?.summary?.warnings && a.summary.warnings.length) {
        toast.info(`${a.summary.warnings.length} Hinweise aus der Analyse vorhanden`);
      } else {
        toast.success('Analyse abgeschlossen');
      }
    } catch (e) {
      // toast in hook; nothing else
    } finally {
      setLoading(false);
    }
  }, [analyze, readFileAsText]);

  // Build flat list of change rows: key = `${userId}::${path}`
  const changeRows = useMemo(() => {
    if (!analysis?.changes) return [];
    const rows = [];
    for (const ch of analysis.changes) {
      for (const d of ch.diffs) {
        rows.push({
          key: `${ch.userId}::${d.path}`,
          userId: ch.userId,
          employeeId: ch.employeeId,
          firstName: ch.firstName,
          lastName: ch.lastName,
          path: d.path,
          label: d.label,
          current: d.current,
          next: d.next,
        });
      }
    }
    return rows;
  }, [analysis]);

  const allChangesSelected =
    changeRows.length > 0 && changeRows.every(r => selectedChangeKeys.has(r.key));
  const allPlannedSelected =
    analysis?.plannedDeactivationMissing?.length > 0 &&
    analysis.plannedDeactivationMissing.every(r => selectedPlanned.has(r.userId));
  const allDeactivateNowSelected =
    analysis?.alreadyResignedButActive?.length > 0 &&
    analysis.alreadyResignedButActive.every(r => selectedDeactivateNow.has(r.userId));
  const allCreateSelected =
    analysis?.toCreate?.length > 0 &&
    analysis.toCreate
      .filter(r => r.actionable)
      .every(r => selectedCreate.has(String(r.employeeId)));
  const allNewEmployeesSelected =
    analysis?.newEmployees?.length > 0 &&
    analysis.newEmployees.every(r => selectedNewEmployees.has(r.userId));

  const toggleSelectAllChanges = useCallback(() => {
    if (allChangesSelected) setSelectedChangeKeys(new Set());
    else setSelectedChangeKeys(new Set(changeRows.map(r => r.key)));
  }, [allChangesSelected, changeRows]);

  const toggleSelectAllPlanned = useCallback(() => {
    if (!analysis?.plannedDeactivationMissing) return;
    if (allPlannedSelected) setSelectedPlanned(new Set());
    else setSelectedPlanned(new Set(analysis.plannedDeactivationMissing.map(r => r.userId)));
  }, [analysis, allPlannedSelected]);

  const toggleSelectAllDeactivateNow = useCallback(() => {
    if (!analysis?.alreadyResignedButActive) return;
    if (allDeactivateNowSelected) setSelectedDeactivateNow(new Set());
    else setSelectedDeactivateNow(new Set(analysis.alreadyResignedButActive.map(r => r.userId)));
  }, [analysis, allDeactivateNowSelected]);

  const toggleSelectAllCreate = useCallback(() => {
    if (!analysis?.toCreate) return;
    const actionable = analysis.toCreate.filter(r => r.actionable);
    if (allCreateSelected) setSelectedCreate(new Set());
    else setSelectedCreate(new Set(actionable.map(r => String(r.employeeId))));
  }, [analysis, allCreateSelected]);

  const toggleSelectAllNewEmployees = useCallback(() => {
    if (!analysis?.newEmployees) return;
    if (allNewEmployeesSelected) setSelectedNewEmployees(new Set());
    else setSelectedNewEmployees(new Set(analysis.newEmployees.map(r => r.userId)));
  }, [analysis, allNewEmployeesSelected]);

  const submit = useCallback(async () => {
    if (!analysis) return;

    // Aggregate selected changes per user
    const byUser = new Map();
    for (const row of changeRows) {
      if (!selectedChangeKeys.has(row.key)) continue;
      if (!byUser.has(row.userId)) byUser.set(row.userId, {});
      const obj = byUser.get(row.userId);
      obj[row.path] =
        row.path === 'birthDate' && row.next ? new Date(row.next).toISOString() : row.next;
    }

    const payload = {
      changes: Array.from(byUser.entries()).map(([userId, set]) => ({ userId, set })),
      ignoreChanges: (() => {
        const byUserIg = new Map();
        for (const row of changeRows) {
          if (!selectedIgnoreChangeKeys.has(row.key)) continue;
          if (!byUserIg.has(row.userId)) byUserIg.set(row.userId, []);
          byUserIg.get(row.userId).push({ path: row.path, value: row.next ?? '' });
        }
        return Array.from(byUserIg.entries()).map(([userId, items]) => ({ userId, items }));
      })(),
      scheduleDeactivation: (analysis.plannedDeactivationMissing || [])
        .filter(r => selectedPlanned.has(r.userId))
        .map(r => ({ userId: r.userId, plannedAt: r.plannedDate })),
      deactivateNow: (analysis.alreadyResignedButActive || [])
        .filter(r => selectedDeactivateNow.has(r.userId))
        .map(r => ({ userId: r.userId })),
      createFromTimebutler: (analysis.toCreate || [])
        .filter(r => r.actionable && selectedCreate.has(String(r.employeeId)))
        .map(r => ({ employeeId: r.employeeId })),
      acceptNewEmployees: (analysis.newEmployees || [])
        .filter(r => selectedNewEmployees.has(r.userId))
        .map(r => ({
          userId: r.userId,
          set: Object.fromEntries((r.fields || []).map(f => [f.path, f.next])),
        })),
    };

    try {
      setLoading(true);
      const result = await execute(payload);
      const okCount =
        (result?.changes || []).filter(x => x.success).length +
        (result?.scheduleDeactivation || []).filter(x => x.success).length +
        (result?.deactivateNow || []).filter(x => x.success).length +
        (result?.createFromTimebutler || []).filter(x => x.success).length;
      const errCount =
        (result?.changes || []).filter(x => !x.success).length +
        (result?.scheduleDeactivation || []).filter(x => !x.success).length +
        (result?.deactivateNow || []).filter(x => !x.success).length +
        (result?.createFromTimebutler || []).filter(x => !x.success).length;
      if (okCount > 0) toast.success(`${okCount} Aktionen erfolgreich ausgeführt`);
      if (errCount > 0) toast.error(`${errCount} Aktionen fehlgeschlagen`);
    } catch (e) {
      // toast in hook
    } finally {
      setLoading(false);
    }
  }, [
    analysis,
    changeRows,
    selectedChangeKeys,
    selectedPlanned,
    selectedDeactivateNow,
    selectedCreate,
    execute,
  ]);

  const headerRowRenderer = section => row => {
    if (row === 'header') {
      if (section === 'changes') {
        return (
          <>
            <TableCell>Ignorieren</TableCell>
            <TableCell>Mitarbeiter</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Feld</TableCell>
            <TableCell>Alt</TableCell>
            <TableCell>Neu</TableCell>
          </>
        );
      }
      if (section === 'planned') {
        return (
          <>
            <TableCell>Mitarbeiter</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Deaktivierungsdatum</TableCell>
          </>
        );
      }
      if (section === 'deactNow') {
        return (
          <>
            <TableCell>Mitarbeiter</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Abmelde-Datum</TableCell>
          </>
        );
      }
      if (section === 'create') {
        return (
          <>
            <TableCell>EmployeeId</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Timebutler</TableCell>
          </>
        );
      }
    }
    return null;
  };

  return (
    <Box sx={{ p: 2, ...viewRootTextPrimarySx }}>
      <Typography variant='h5' gutterBottom>
        CSV Sync-Import
      </Typography>
      <Stack direction='row' spacing={1} alignItems='center' mb={1.5}>
        <input type='file' ref={fileRef} accept='.csv,text/csv' />
        <Button variant='contained' onClick={onUpload} disabled={loading}>
          {loading ? 'Analysiere…' : 'Analyse starten'}
        </Button>
      </Stack>

      {analysis && (
        <Box>
          <Typography variant='caption' sx={{ opacity: 0.8 }} gutterBottom>
            {analysis.summary?.rows} Zeilen, {analysis.summary?.matchedUsers} passende Nutzer
          </Typography>

          <Section
            title='Neue Mitarbeiter'
            rows={analysis.newEmployees || []}
            countLabel={`${(analysis.newEmployees || []).length} Einträge`}
            allSelected={allNewEmployeesSelected}
            onSelectAll={toggleSelectAllNewEmployees}
            renderRow={row => {
              if (row === 'header')
                return (
                  <>
                    <TableCell>Mitarbeiter</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Felder (aus CSV)</TableCell>
                  </>
                );
              const checked = selectedNewEmployees.has(row.userId);
              return (
                <TableRow key={row.userId} hover>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      onChange={e => {
                        const next = new Set(selectedNewEmployees);
                        if (e.target.checked) next.add(row.userId);
                        else next.delete(row.userId);
                        setSelectedNewEmployees(next);
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.employeeId}</TableCell>
                  <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`.trim()}</TableCell>
                  <TableCell>
                    {(row.fields || [])
                      .map(f => `${f.label}${f.next ? `: ${f.next}` : ''}`)
                      .join(', ')}
                  </TableCell>
                </TableRow>
              );
            }}
          />

          <Section
            title='Änderungen'
            rows={changeRows}
            countLabel={`${changeRows.length} mögliche Änderungen`}
            allSelected={allChangesSelected}
            onSelectAll={toggleSelectAllChanges}
            renderRow={row => {
              if (row === 'header') return headerRowRenderer('changes')(row);
              const checked = selectedChangeKeys.has(row.key);
              return (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      onChange={e => {
                        const next = new Set(selectedChangeKeys);
                        if (e.target.checked) next.add(row.key);
                        else next.delete(row.key);
                        setSelectedChangeKeys(next);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={selectedIgnoreChangeKeys.has(row.key)}
                      onChange={e => {
                        const next = new Set(selectedIgnoreChangeKeys);
                        if (e.target.checked) next.add(row.key);
                        else next.delete(row.key);
                        setSelectedIgnoreChangeKeys(next);
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.employeeId}</TableCell>
                  <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`.trim()}</TableCell>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.current || ''}</TableCell>
                  <TableCell>{row.next || ''}</TableCell>
                </TableRow>
              );
            }}
          />

          <Section
            title='Geplante Abmeldung nicht eingetragen'
            rows={analysis.plannedDeactivationMissing || []}
            countLabel={`${(analysis.plannedDeactivationMissing || []).length} Einträge`}
            allSelected={allPlannedSelected}
            onSelectAll={toggleSelectAllPlanned}
            renderRow={row => {
              if (row === 'header') return headerRowRenderer('planned')(row);
              const checked = selectedPlanned.has(row.userId);
              return (
                <TableRow key={row.userId} hover>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      onChange={e => {
                        const next = new Set(selectedPlanned);
                        if (e.target.checked) next.add(row.userId);
                        else next.delete(row.userId);
                        setSelectedPlanned(next);
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.employeeId}</TableCell>
                  <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`.trim()}</TableCell>
                  <TableCell>{new Date(row.plannedDate).toLocaleDateString('de-DE')}</TableCell>
                </TableRow>
              );
            }}
          />

          <Section
            title='Bereits abgemeldet, aber noch aktiv'
            rows={analysis.alreadyResignedButActive || []}
            countLabel={`${(analysis.alreadyResignedButActive || []).length} Einträge`}
            allSelected={allDeactivateNowSelected}
            onSelectAll={toggleSelectAllDeactivateNow}
            renderRow={row => {
              if (row === 'header') return headerRowRenderer('deactNow')(row);
              const checked = selectedDeactivateNow.has(row.userId);
              return (
                <TableRow key={row.userId} hover>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      onChange={e => {
                        const next = new Set(selectedDeactivateNow);
                        if (e.target.checked) next.add(row.userId);
                        else next.delete(row.userId);
                        setSelectedDeactivateNow(next);
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.employeeId}</TableCell>
                  <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`.trim()}</TableCell>
                  <TableCell>
                    {row.missingInCsv
                      ? 'In CSV nicht enthalten'
                      : row.resignedAt
                        ? new Date(row.resignedAt).toLocaleDateString('de-DE')
                        : ''}
                  </TableCell>
                </TableRow>
              );
            }}
          />

          <Section
            title='Angemeldet, aber kein Account'
            rows={analysis.toCreate || []}
            countLabel={`${(analysis.toCreate || []).length} Einträge`}
            allSelected={allCreateSelected}
            onSelectAll={toggleSelectAllCreate}
            renderRow={row => {
              if (row === 'header') return headerRowRenderer('create')(row);
              const key = String(row.employeeId);
              const checked = selectedCreate.has(key);
              return (
                <TableRow key={key} hover sx={{ opacity: row.actionable ? 1 : 0.5 }}>
                  <TableCell>
                    <Checkbox
                      disabled={!row.actionable}
                      checked={checked}
                      onChange={e => {
                        const next = new Set(selectedCreate);
                        if (e.target.checked) next.add(key);
                        else next.delete(key);
                        setSelectedCreate(next);
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.employeeId}</TableCell>
                  <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`.trim()}</TableCell>
                  <TableCell>{row.tbUserId ? `TB:${row.tbUserId}` : 'kein TB-Datensatz'}</TableCell>
                </TableRow>
              );
            }}
          />

          <Stack direction='row' spacing={1}>
            <Button variant='contained' onClick={submit} disabled={loading}>
              {loading ? 'Wende an…' : 'Ausgewählte Aktionen ausführen'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default SpecialSyncImport;
